import React from 'react';
import { useTranslation } from 'react-i18next';
import { NetworkStatus } from 'apollo-client';

import { connect } from 'react-redux';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useSnackbar } from 'notistack';
import { initialize } from 'redux-form';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { Tab, Typography } from '@material-ui/core';
import Tabs from '@material-ui/core/Tabs';
import Button from '@material-ui/core/Button';

import {
  AppHeader,
  RadioButtonsGroup,
  TabPanel,
  CircularLoader,
  SelectDropDown,
  SelectCompanyBox
} from 'components';

import Instructions from './components/instructions/Instructions';
import WidgetSettingsForm from './components/widget-form/WidgetForm';

import { CURRENT_BUSINESS_OWNER_INFO } from 'graphql/queries/business-owner';
import { WIDGET_SETTINGS } from 'graphql/queries/widget';
import { USER_SETTINGS } from 'graphql/queries/settings';

import { getDataToStore } from 'utils/widget/getDataToStore';
import {
  prepareReceivedWidgetData,
  prepareUnitsDataForSelect
} from 'utils/widget/prepareReceivedData';

import {
  CREATE_WIDGET_SETTINGS,
  UPDATE_WIDGET_SETTINGS
} from 'graphql/mutations/widget';

import { generateWidgetButton } from 'utils/widget/generateWidgetButton';

import useStyles from './styles';

function verticalTabProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`
  };
}

const PRODUCT_LIST_INDEX = 0;
const SPECIFIC_UNIT_INDEX = 1;

const ACCOMMODATION_UNIT_SELECT_TYPE = 'Accommodation';
const ACTIVITY_UNIT_SELECT_TYPE = 'Activity';

const DEFAULT_COLOR = '#0E8BB4';

let WidgetSettings = ({ ...props }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const { initializeForm, businessOwner } = props;

  const [value, setValue] = React.useState(0);
  const [color, setColor] = React.useState(DEFAULT_COLOR);
  const [isLoading, setIsLoading] = React.useState(true);
  const [savedForms, setSavedForms] = React.useState({});
  const [unitType, setUnitType] = React.useState('Accommodation');
  const [isUpdate, setIsUpdate] = React.useState(false);
  const [selectedUnitId, setSelectedUnitId] = React.useState('');

  const [accommodations, setAccommodations] = React.useState([]);
  const [activities, setActivities] = React.useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const [createWidgetSettings] = useMutation(CREATE_WIDGET_SETTINGS);
  const [updateWidgetSettings] = useMutation(UPDATE_WIDGET_SETTINGS);

  const { data, refetch: refetchWidgetSettings, networkStatus } = useQuery(
    WIDGET_SETTINGS,
    {
      variables: {
        businessOwnerId:
          businessOwner.selectedBusinessOwner.ownerId ||
          businessOwner.selectedBusinessOwner.id
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  const { loading: userSettingsLoading, data: userSettingsData } = useQuery(
    USER_SETTINGS,
    {
      variables: {
        businessOwnerId:
          businessOwner.selectedBusinessOwner.ownerId ||
          businessOwner.selectedBusinessOwner.id
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  const { loading: businessOwnerLoading, data: businessOwnerData } = useQuery(
    CURRENT_BUSINESS_OWNER_INFO,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  React.useEffect(() => {
    if (data) {
      if (networkStatus === NetworkStatus.refetch) return;
      setIsLoading(false);
      const widgetData = prepareReceivedWidgetData(data.widget);
      const unitsData = prepareUnitsDataForSelect(data);

      setSavedForms({
        ...widgetData,
        color: widgetData.color || DEFAULT_COLOR
      });

      setIsUpdate(!!widgetData.id);

      let initialValues = {
        widgetForm: widgetData
      };

      setColor(widgetData.color ? widgetData.color : DEFAULT_COLOR);
      setAccommodations(unitsData.accommodations);
      setActivities(unitsData.activities);

      initializeForms(initialValues);
    }
  }, [data]);

  const isPaymentsSettings = isMessage => {
    if (!userSettingsData.payPalSettings && !userSettingsData.stripeSettings) {
      const payOnArrivalPossibility = userSettingsData.payOnArrivalPossibility;
      if (payOnArrivalPossibility && payOnArrivalPossibility.payOnArrival) {
        return true;
      }

      if (isMessage) {
        enqueueSnackbar(
          t(
            'Code can’t be generated because the payment system isn’t connected'
          ),
          {
            variant: 'error'
          }
        );
      }

      return false;
    }

    return true;
  };

  const widgetSettingSubmit = values => {
    const dataToStore = getDataToStore(
      values,
      businessOwner.selectedBusinessOwner.ownerId ||
        businessOwner.selectedBusinessOwner.id
    );
    setIsLoading(true);
    if (isUpdate) {
      updateWidgetSettings({ variables: dataToStore }).then(() => {
        enqueueSnackbar(t('Widget settings have been successfully updated'), {
          variant: 'success'
        });
        refetchWidgetSettings();
      });
    } else {
      createWidgetSettings({ variables: dataToStore }).then(() => {
        enqueueSnackbar(t('Widget settings have been successfully created'), {
          variant: 'success'
        });
        refetchWidgetSettings();
      });
    }
  };

  const initializeForms = initialFormsData => {
    Object.keys(initialFormsData).map(field => {
      initializeForm(field, initialFormsData[field]);
    });
  };

  const copyButtonCode = () => {
    if (!userSettingsData || !isPaymentsSettings(false) || !savedForms) return;
    const allItems = value === PRODUCT_LIST_INDEX;

    const businessOwnerId =
      businessOwner && businessOwner.selectedBusinessOwner
        ? businessOwner.selectedBusinessOwner.ownerId ||
          businessOwner.selectedBusinessOwner.id
        : null;

    const googleAnalyticsId =
      businessOwnerData && businessOwnerData.googleAnalyticsSettings
        ? businessOwnerData.googleAnalyticsSettings.googleAnalyticsId
        : null;

    if (allItems) {
      return generateWidgetButton({
        businessOwnerId: businessOwnerId,
        color: savedForms.color || DEFAULT_COLOR,
        googleAnalyticsId: googleAnalyticsId,
        languages: savedForms.languages,
        logo: savedForms.coverPhoto.image,
        termsAndConditions: savedForms.termsAndConditions,
        privacyPolicy: savedForms.privacyPolicy
      });
    }

    const itemType =
      unitType === ACTIVITY_UNIT_SELECT_TYPE && !allItems
        ? ACTIVITY_UNIT_SELECT_TYPE
        : ACCOMMODATION_UNIT_SELECT_TYPE;

    return generateWidgetButton({
      businessOwnerId: businessOwnerId,
      color: savedForms.color || DEFAULT_COLOR,
      googleAnalyticsId: googleAnalyticsId,
      languages: savedForms.languages,
      logo: savedForms.coverPhoto.image,
      termsAndConditions: savedForms.termsAndConditions,
      privacyPolicy: savedForms.privacyPolicy,
      productId: selectedUnitId,
      itemType: itemType
    });
  };

  return (
    <div className={classes.root}>
      <AppHeader />
      <CircularLoader
        isLoading={userSettingsLoading || businessOwnerLoading || isLoading}
        loaderClassName={classes.loaderClassName}
      />
      {!businessOwner.selectedBusinessOwner.id && <SelectCompanyBox />}
      {businessOwner.selectedBusinessOwner.id && (
        <div className={classes.toolbar}>
          <div className={classes.toolbarLeft}>
            <Typography variant="h2" className={classes.toolbarTitle}>
              {t('Widget')}
            </Typography>
          </div>
        </div>
      )}
      {businessOwner.selectedBusinessOwner.id && (
        <div className={classes.content}>
          <div className={classes.codePanel}>
            <Typography className={classes.panelTitle}>{t('Code')}</Typography>
            <div className={classes.switchers}>
              <Tabs
                classes={{ root: classes.tabs }}
                indicatorColor="secondary"
                value={value}
                onChange={handleChange}
              >
                <Tab
                  classes={{
                    root: classes.customTabRoot,
                    wrapper: classes.customTabWrapper,
                    selected: classes.selected,
                    textColorInherit: classes.textColorInheritCustom
                  }}
                  label={t('Product list')}
                  {...verticalTabProps(PRODUCT_LIST_INDEX)}
                />
                <Tab
                  classes={{
                    root: classes.customTabRoot,
                    wrapper: classes.customTabWrapper,
                    selected: classes.selected,
                    textColorInherit: classes.textColorInheritCustom
                  }}
                  label={t('Specific unit')}
                  {...verticalTabProps(SPECIFIC_UNIT_INDEX)}
                />
              </Tabs>
            </div>

            <CopyToClipboard
              onCopy={() => {
                if (!isPaymentsSettings(false)) return;
                enqueueSnackbar(t('Copied'), {
                  variant: 'success'
                });
              }}
              text={copyButtonCode()}
            >
              <Button
                disabled={
                  (value === SPECIFIC_UNIT_INDEX && !selectedUnitId) ||
                  !savedForms
                }
                variant="outlined"
                color="primary"
                onClick={() => isPaymentsSettings(true)}
                classes={{ root: classes.buttonRoot }}
              >
                {t('Generate and copy')}
              </Button>
            </CopyToClipboard>

            <TabPanel value={value} index={PRODUCT_LIST_INDEX}>
              <Instructions />
            </TabPanel>

            <TabPanel value={value} index={SPECIFIC_UNIT_INDEX}>
              <div className={classes.unitSelectWrapper}>
                <RadioButtonsGroup
                  onChange={setUnitType}
                  input={{
                    value: unitType,
                    onChange: val => {
                      setUnitType(val);
                      setSelectedUnitId('');
                    }
                  }}
                  list={['Accommodation', 'Activity']}
                />
                {(accommodations.length !== 0 || activities.length !== 0) && (
                  <SelectDropDown
                    className={classes.selectInput}
                    value={selectedUnitId}
                    placeholder="Select"
                    onChange={setSelectedUnitId}
                    items={
                      unitType === ACCOMMODATION_UNIT_SELECT_TYPE
                        ? accommodations
                        : activities
                    }
                  />
                )}
              </div>
              <Instructions />
            </TabPanel>
          </div>
          {data && (
            <WidgetSettingsForm
              color={color}
              colorHandler={setColor}
              onSubmit={values => widgetSettingSubmit(values)}
            />
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm,
    businessOwner: state.businessOwner
  };
};
const mapDispatchToProps = dispatch => ({
  initializeForm: (form, data) => {
    dispatch(initialize(form, data));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetSettings);
